import { useState, useEffect, useRef, useContext } from "react";
import useAgora from "../../../hooks/useAgora";
import VideoPlayer from "./VideoPlayer";
import "../../../assets/css/student/studycalendar/GroupClass.css";
import AppContext from "../../../context/AppContext";
import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";

// ─── Icons ───────────────────────────────────────────────────────────────────
const MicIcon = ({ muted }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
    {muted ? (
      <>
        <path d="M19 11a7 7 0 0 1-.19 1.63l-1.45-1.45A5 5 0 0 0 17 11V5a5 5 0 0 0-10 0v.17L5.17 3.34A7 7 0 0 1 19 5v6Z" />
        <path d="M12 18a6 6 0 0 1-5.93-5.07L4.5 11.36A8 8 0 0 0 11 18.93V21H8v2h8v-2h-3v-2.07A8 8 0 0 0 20 11h-2a6 6 0 0 1-6 7Z" />
        <line x1="3" y1="3" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
      </>
    ) : (
      <>
        <path d="M12 1a4 4 0 0 1 4 4v6a4 4 0 0 1-8 0V5a4 4 0 0 1 4-4Z" />
        <path d="M19 11a7 7 0 0 1-14 0M12 18v3M8 21h8" />
      </>
    )}
  </svg>
);

const CameraIcon = ({ muted }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    {muted ? (
      <>
        <path d="M16 16v1a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2h2M22 8l-6 4 6 4V8Z" />
        <line x1="1" y1="1" x2="23" y2="23" />
      </>
    ) : (
      <>
        <path d="M23 7 16 12l7 5V7Z" />
        <rect x="1" y="5" width="15" height="14" rx="2" ry="2" />
      </>
    )}
  </svg>
);

const ScreenIcon = ({ sharing }) => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <rect x="2" y="3" width="20" height="14" rx="2" ry="2" />
    <line x1="8" y1="21" x2="16" y2="21" />
    <line x1="12" y1="17" x2="12" y2="21" />
    {sharing && <circle cx="12" cy="10" r="3" fill="currentColor" />}
  </svg>
);

const PhoneOffIcon = () => (
  <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M10.68 13.31a16 16 0 0 0 3.41 2.6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7 2 2 0 0 1 1.72 2v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07" />
    <path d="M14.5 2.5a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L15.48 10.5" />
    <line x1="23" y1="1" x2="1" y2="23" />
  </svg>
);

const UsersIcon = () => (
  <svg viewBox="0 0 24 24" width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
    <circle cx="9" cy="7" r="4" />
    <path d="M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75" />
  </svg>
);

// ─── Avatar Placeholder ───────────────────────────────────────────────────────
const AvatarPlaceholder = ({ uid, label }) => {
  const colors = ["#e63946", "#2a9d8f", "#e9c46a", "#f4a261", "#457b9d", "#6a4c93"];
  const color = colors[uid % colors.length];
  const initial = label ? label[0].toUpperCase() : "?";
  return (
    <div className="avatar-placeholder" style={{ background: `${color}22`, borderColor: `${color}66` }}>
      <div className="avatar-circle" style={{ background: color }}>
        {initial}
      </div>
      <p className="avatar-uid">{label || `User ${uid}`}</p>
    </div>
  );
};

// ─── Remote User Tile ─────────────────────────────────────────────────────────
const RemoteUserTile = ({ user }) => {
  const hasVideo = !!user.videoTrack;
  const hasAudio = !!user.audioTrack;

  return (
    <div className="video-tile remote-tile">
      {hasVideo ? (
        <VideoPlayer
          videoTrack={user.videoTrack}
          audioTrack={user.audioTrack}
          className="video-element"
        />
      ) : (
        <AvatarPlaceholder uid={user.uid} label={`User ${user.uid}`} />
      )}
      <div className="tile-info">
        <span className="tile-name">User {user.uid}</span>
        {!hasAudio && <span className="muted-badge">🔇</span>}
      </div>
    </div>
  );
};

// ─── Join Screen ──────────────────────────────────────────────────────────────
const JoinScreen = ({ onJoin, isLoading }) => {
  const { baseUrl, loggedInStudent,selectedSessionForVideocall } = useContext(AppContext);
  const { title } = useParams();
  const navigate = useNavigate();
  const [appId, setAppId] = useState("");
  const [channel, setChannel] = useState(title);
  const [token, setToken] = useState("");
  const [name, setName] = useState("");
  // "testing" = static key (no token), "secure" = dynamic key (token required)
  const [authMode, setAuthMode] = useState("secure");

  const handleSubmit = () => {
    if (!appId.trim() || !channel.trim()) return;
    if (authMode === "secure" && !token.trim()) return;
    onJoin({
      appId: appId.trim(),
      channel: channel.trim(),
      // Pass null for testing/static-key mode — never pass empty string
      token: authMode === "secure" ? token.trim() : null,
      name,
    });
  };

  const meetingDetails = () => {
    console.log(loggedInStudent);
    const formData = new FormData();
    formData.append("channel", channel);
    formData.append("chstudent_id", loggedInStudent.id);

    axios.post(`${baseUrl}/student/studySessions/startVideoCallingSession`, formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      }
    }).then((response) => {
      console.log(response.data);
      if(response.data.hasOwnProperty("auth"))
      {
        navigate("/login")
      }else
      {
        setAppId(response.data.appId);
        setToken(response.data.token);
        setChannel(response.data.channel);
        setName(loggedInStudent == undefined || loggedInStudent == null ? "Annonymous" : loggedInStudent.fullname);
      }
    })
  }

  useEffect(() => {
    setTimeout(()=>{
        meetingDetails();
    },1000);
  }, [])

  return (
    <div className="join-screen">
      <div className="join-card">
        <div className="join-header">
          <div className="join-logo">
            <ScreenIcon />
          </div>
          <h1>Join Session</h1>
          <p>as</p>
          <h2 className="text-white">{name}</h2>
          {/* <p>Powered by NIMRA, PAKEEZA AND BISMA</p> */}
        </div>

        <div className="join-form">
          {/* Auth Mode Selector */}


          <div className="form-group">
            {/* <label>Agora App ID *</label> */}
            <input
              type="hidden"
              value={appId}
              onChange={(e) => setAppId(e.target.value)}
              placeholder="e.g. a1b2c3d4e5f6..."
            />
          </div>

          <div className="form-group">
            {/* <label>Channel Name *</label> */}
            <input
              type="hidden"
              value={channel}
              onChange={(e) => setChannel(e.target.value)}
              placeholder="e.g. math-class-101"
            />
          </div>

          {authMode === "secure" && (
            <div className="form-group">
              {/* <label>Token *</label> */}
              <input
                type="hidden"
                value={token}
                onChange={(e) => setToken(e.target.value)}
                placeholder="Paste your Agora token here"
              />
            </div>
          )}

          {/* <div className="form-group">
            <label>Your Name <span className="optional">(optional)</span></label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Enter your display name"
            />
          </div> */}

          <button
            className="join-btn"
            onClick={handleSubmit}
            disabled={
              !appId.trim() ||
              !channel.trim() ||
              (authMode === "secure" && !token.trim()) ||
              isLoading
            }
          >
            {isLoading ? (
              <><span className="spinner" /> Joining...</>
            ) : (
              "Join Class →"
            )}
          </button>
        </div>

        <div className="join-features">
          <span>🎙 Audio</span>
          <span>📹 Video</span>
          <span>🖥 Screen Share</span>
        </div>
      </div>
    </div>
  );
};

// ─── Main Group Class Component ────────────────────────────────────────────────
const GroupClass = () => {
  const [sessionConfig, setSessionConfig] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [joinError, setJoinError] = useState(null);
  const [layout, setLayout] = useState("grid"); // grid | spotlight

  const agora = useAgora(
    sessionConfig || { appId: "", channel: "", token: null, uid: null }
  );

  const localVideoRef = useRef(null);

  // Play local video
  useEffect(() => {
    if (!agora.localTracks.videoTrack || !localVideoRef.current) return;
    if (agora.isSharingScreen) return;
    agora.localTracks.videoTrack.play(localVideoRef.current);
    return () => agora.localTracks.videoTrack?.stop();
  }, [agora.localTracks.videoTrack, agora.isSharingScreen]);

  // Play local screen track in local tile
  const screenRef = useRef(null);
  useEffect(() => {
    if (!agora.screenTrack || !screenRef.current) return;
    agora.screenTrack.play(screenRef.current);
    return () => agora.screenTrack?.stop();
  }, [agora.screenTrack]);

  const handleJoin = async ({ appId, channel, token }) => {
    setIsLoading(true);
    setJoinError(null);
    setSessionConfig({ appId, channel, token, uid: null });
  };

  // Once config is set, trigger join
  useEffect(() => {
    if (!sessionConfig || agora.joined) return;
    agora.join().catch((err) => {
      setJoinError(err.message);
      setSessionConfig(null);
    }).finally(() => setIsLoading(false));
  }, [sessionConfig]);

  const handleLeave = async () => {
    await agora.leave();
    setSessionConfig(null);
    setJoinError(null);
  };

  if (!agora.joined) {
    return (
      <JoinScreen
        onJoin={handleJoin}
        isLoading={isLoading}
        error={joinError}
      />
    );
  }

  const totalParticipants = agora.remoteUsers.length + 1;

  return (
    <div className="class-room">
      {/* ── Header ── */}
      <header className="room-header">
        <div className="header-left">
          <div className="status-dot" />
          <span className="channel-name">{sessionConfig?.channel}</span>
        </div>
        <div className="header-center">
          <button
            className={`layout-btn ${layout === "grid" ? "active" : ""}`}
            onClick={() => setLayout("grid")}
            title="Grid view"
          >
            ⊞
          </button>
          <button
            className={`layout-btn ${layout === "spotlight" ? "active" : ""}`}
            onClick={() => setLayout("spotlight")}
            title="Spotlight view"
          >
            ⊡
          </button>
        </div>
        <div className="header-right">
          <UsersIcon />
          <span>{totalParticipants}</span>
        </div>
      </header>

      {/* ── Video Grid ── */}
      <main className={`video-grid ${layout} participants-${Math.min(totalParticipants, 6)}`}>
        {/* Local User */}
        <div className={`video-tile local-tile ${layout === "spotlight" ? "spotlight-main" : ""}`}>
          {agora.isSharingScreen ? (
            <div ref={screenRef} className="video-element" />
          ) : agora.isVideoMuted ? (
            <AvatarPlaceholder uid={0} label="You" />
          ) : (
            <div ref={localVideoRef} className="video-element" />
          )}
          <div className="tile-info">
            <span className="tile-name">You {agora.isSharingScreen && "(Screen)"}</span>
            {agora.isAudioMuted && <span className="muted-badge">🔇</span>}
          </div>
          <span className="local-badge">Local</span>
        </div>

        {/* Remote Users */}
        {agora.remoteUsers.map((user) => (
          <RemoteUserTile key={user.uid} user={user} />
        ))}

        {/* Empty state */}
        {agora.remoteUsers.length === 0 && (
          <div className="waiting-tile">
            <div className="waiting-icon">⏳</div>
            <p>Waiting for others to join...</p>
            <small>Share the channel name: <strong>{sessionConfig?.channel}</strong></small>
          </div>
        )}
      </main>

      {/* ── Controls ── */}
      <footer className="controls-bar">
        <button
          className={`ctrl-btn ${agora.isAudioMuted ? "ctrl-danger" : ""}`}
          onClick={agora.toggleAudio}
          title={agora.isAudioMuted ? "Unmute" : "Mute"}
        >
          <MicIcon muted={agora.isAudioMuted} />
          <span>{agora.isAudioMuted ? "Unmute" : "Mute"}</span>
        </button>

        <button
          className={`ctrl-btn ${agora.isVideoMuted ? "ctrl-danger" : ""}`}
          onClick={agora.toggleVideo}
          title={agora.isVideoMuted ? "Start Video" : "Stop Video"}
        >
          <CameraIcon muted={agora.isVideoMuted} />
          <span>{agora.isVideoMuted ? "Start Video" : "Stop Video"}</span>
        </button>

        <button
          className={`ctrl-btn ${agora.isSharingScreen ? "ctrl-active" : ""}`}
          onClick={agora.toggleScreenShare}
          title={agora.isSharingScreen ? "Stop Sharing" : "Share Screen"}
        >
          <ScreenIcon sharing={agora.isSharingScreen} />
          <span>{agora.isSharingScreen ? "Stop Share" : "Share Screen"}</span>
        </button>

        <button className="ctrl-btn ctrl-leave" onClick={handleLeave} title="Leave class">
          <PhoneOffIcon />
          <span>Leave</span>
        </button>
      </footer>

      {/* Error Toast */}
      {agora.error && (
        <div className="error-toast">
          ⚠ {agora.error}
        </div>
      )}
    </div>
  );
};

export default GroupClass;